// Live-draft preview plumbing, lets the Website admin show UNSAVED changes on
// the real site instantly.
//
// RECEIVER (this module): when the venue site runs inside an IFRAME on the same
// origin (the admin's live preview), it listens for postMessage drafts and
// exposes them as an appConfig OVERLAY that useVenue() merges over the fetched
// config. Nothing is written anywhere, close the preview and the draft is
// gone; the live site for real visitors is untouched until Save.
//
// Security: the listener only attaches when framed, and messages are accepted
// from the SAME ORIGIN only (the admin lives on the venue's own subdomain).
import { useSyncExternalStore } from "react";
import type { VenueAppConfig } from "./types";

export const PREVIEW_MSG_TYPE = "assembly:preview-config";
// The editor can also drive which URL the preview iframe is on, so selecting
// the "Contact" page tab shows /contact inside the frame, even for pages that
// don't yet exist in the saved config (draft-only additions).
export const PREVIEW_NAVIGATE_MSG_TYPE = "assembly:preview-navigate";
// REVERSE direction (iframe → parent editor): when the operator drags a
// freeform-canvas item directly on the preview, the iframe emits this so the
// admin editor updates the block config and pushes the new draft back into the
// iframe (via PREVIEW_MSG_TYPE) so the preview keeps rendering the truth.
// Toka 2026-09-19, Squarespace-style in-preview editing.
export const PREVIEW_EDIT_MSG_TYPE = "assembly:preview-edit";

/**
 * Message the iframe posts to the parent when an in-preview drag/resize
 * completes. Parent looks up the item by id across every freeform block on
 * the currently-previewed page and applies the patch.
 *
 * `commit` distinguishes an interim visual update (during drag, applied
 * locally in the iframe via CSS) from a state-changing commit on pointerup.
 * v1 only sends commit messages; interim drag stays local for perf.
 */
export type PreviewEditMessage = {
  type: typeof PREVIEW_EDIT_MSG_TYPE;
  itemId: string;
  patch: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    rotation?: number;
  };
  commit: boolean;
};

// Whole-block admin overlay in the preview (Webflow/Squarespace-style
// "hover any block, get a toolbar"). Sent iframe → parent editor.
// - REORDER: move the block up or down in its page's block list.
// - DELETE: remove the block.
// - SELECT: scroll the left-panel editor to that block's form + highlight it,
//   so the operator can edit its content (text, image, colours) without
//   leaving the preview.
export const PREVIEW_REORDER_MSG_TYPE = "assembly:preview-reorder";
export const PREVIEW_DELETE_MSG_TYPE = "assembly:preview-delete";
export const PREVIEW_SELECT_MSG_TYPE = "assembly:preview-select";

export type PreviewReorderMessage = {
  type: typeof PREVIEW_REORDER_MSG_TYPE;
  blockIndex: number;
  /** -1 moves up, +1 moves down. */
  delta: -1 | 1;
};

export type PreviewDeleteMessage = {
  type: typeof PREVIEW_DELETE_MSG_TYPE;
  blockIndex: number;
};

export type PreviewSelectMessage = {
  type: typeof PREVIEW_SELECT_MSG_TYPE;
  blockIndex: number;
};

// Whole-SECTION click-to-edit (as opposed to a single block): the operator
// clicks the site footer or the coming-soon landing page in the preview, we
// open the corresponding accordion on the admin page. Section ids match the
// `id` prop on CollapsibleSection ("site-pages", "site-footer", "landing-
// page", "your-website", etc.).
export const PREVIEW_SELECT_SECTION_MSG_TYPE = "assembly:preview-select-section";

export type PreviewSelectSectionMessage = {
  type: typeof PREVIEW_SELECT_SECTION_MSG_TYPE;
  sectionId: string;
};

// Gallery image reorder from the preview. When the operator drags an image
// on the rendered gallery + drops it, we emit the from/to indices; the
// parent editor moves the URL inside that block's `images` array.
export const PREVIEW_GALLERY_REORDER_MSG_TYPE = "assembly:preview-gallery-reorder";
export type PreviewGalleryReorderMessage = {
  type: typeof PREVIEW_GALLERY_REORDER_MSG_TYPE;
  blockIndex: number;
  from: number;
  to: number;
};

// Generic inside-a-block item reorder: works for any block that holds a
// list (tiles, agenda, faq, audio, eventsCarousel, columns cells). The
// parent picks the target array by block type. Kept separate from the
// gallery-specific one because gallery images live in `images`, not
// `items` — same shape, different array name.
export const PREVIEW_ITEM_REORDER_MSG_TYPE = "assembly:preview-item-reorder";
export type PreviewItemReorderMessage = {
  type: typeof PREVIEW_ITEM_REORDER_MSG_TYPE;
  blockIndex: number;
  from: number;
  to: number;
};

let overlay: Partial<VenueAppConfig> | null = null;
const subs = new Set<() => void>();
// Flipped true the first time we receive a PREVIEW_MSG_TYPE, confirming
// this iframe is our own admin preview (not a random third-party embed).
// Interactive-editing features gate on this so a public site iframed
// elsewhere never accidentally exposes edit chrome.
let inAdminPreview = false;
const previewFlagSubs = new Set<() => void>();

if (typeof window !== "undefined" && window.self !== window.top) {
  window.addEventListener("message", (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return;
    const d = e.data as { type?: string; appConfig?: Partial<VenueAppConfig>; path?: string } | null;
    if (!d) return;
    if (d.type === PREVIEW_MSG_TYPE) {
      overlay = d.appConfig && Object.keys(d.appConfig).length > 0 ? d.appConfig : null;
      if (!inAdminPreview) {
        inAdminPreview = true;
        previewFlagSubs.forEach((f) => f());
      }
      subs.forEach((f) => f());
      return;
    }
    if (d.type === PREVIEW_NAVIGATE_MSG_TYPE && typeof d.path === "string") {
      const target = d.path.startsWith("/") ? d.path : `/${d.path}`;
      if (target !== window.location.pathname) {
        // pushState + a fresh PopStateEvent, how TanStack Router's browser
        // history picks up an external URL change without a full reload.
        window.history.pushState({}, "", target);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    }
  });
}

/** The draft appConfig overlay (null outside the admin preview iframe). */
export function usePreviewOverlay(): Partial<VenueAppConfig> | null {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => overlay,
    () => null,
  );
}

/**
 * True only inside our own admin preview iframe: framed AND has received at
 * least one same-origin preview message. Never true for public visitors or
 * third-party embeds. Interactive-editing features (Squarespace-style in-
 * preview drag) gate on this to avoid exposing edit chrome outside admin.
 */
export function useInAdminPreview(): boolean {
  return useSyncExternalStore(
    (cb) => {
      previewFlagSubs.add(cb);
      return () => previewFlagSubs.delete(cb);
    },
    () => inAdminPreview,
    () => false,
  );
}
